import React, { Suspense, Fragment, lazy } from "react";
import { Routes, Navigate, Route } from "react-router-dom";

import Loader from "./components/Loader/Loader";
import AdminLayout from "./layouts/AdminLayout";

import GuestGuard from "./components/Auth/GuestGuard";
import StartInfoGuard from "./components/Auth/StartInfoGuard";
import AuthGuard from "./components/Auth/AuthGuard";
import { RolesGuard } from "./components/Auth/RolesGuard";

import { BASE_URL } from "./config/constant";
import { TigerySoftGuard } from "./components/Auth/tigerySoftGuard";

const renderRoutes = (routes = []) => (
  <Suspense fallback={<Loader />}>
    <Routes>
      {routes.map((route, i) => {
        const Guard = route.guard || Fragment;
        const Layout = route.layout || Fragment;
        const Element = route.element;
        // eslint-disable-next-line
        const roles = route.roles || [];
        return (
          <Route
            key={i}
            path={route.path}
            exact={route.exact}
            element={
              // Solo pasar roles al Guard si existe
              Guard !== Fragment ? (
                <Guard roles={route.roles} module={route.module}>
                  <Layout>
                    {route.routes ? (
                      renderRoutes(route.routes)
                    ) : (
                      <Element props={true} />
                    )}
                  </Layout>
                </Guard>
              ) : (
                <Layout>
                  {route.routes ? (
                    renderRoutes(route.routes)
                  ) : (
                    <Element props={true} />
                  )}
                </Layout>
              )
            }
          />
        );
      })}
    </Routes>
  </Suspense>
);

export const routes = [
  {
    exact: "true",
    guard: GuestGuard,
    path: "/auth/signin",
    element: lazy(() => import("./views/auth/signin/SignIn1")),
  },
  {
    exact: "true",
    path: "/404",
    element: lazy(() => import("./views/errors/NotFound404")),
  },
  {
    exact: "true",
    path: "/maintenance/coming-soon",
    element: lazy(() => import("./views/maintenance/ComingSoon")),
  },
  {
    exact: "true",
    path: "/maintenance/error",
    element: lazy(() => import("./views/maintenance/Error")),
  },
  {
    exact: "true",
    path: "/maintenance/offline-ui",
    element: lazy(() => import("./views/maintenance/OfflineUI")),
  },
  {
    exact: "true",
    path: "/auth/signup-1",
    element: lazy(() => import("./views/auth/signup/SignUp1")),
  },
  {
    exact: "true",
    path: "/auth/signup-2",
    element: lazy(() => import("./views/auth/signup/SignUp2")),
  },
  {
    exact: "true",
    path: "/auth/signin-1",
    element: lazy(() => import("./views/auth/signin/SignIn1")),
  },
  {
    exact: "true",
    path: "/auth/signin-2",
    element: lazy(() => import("./views/auth/signin/SignIn2")),
  },
  {
    exact: "true",
    path: "/auth/reset-password-1",
    element: lazy(() => import("./views/auth/reset-password/ResetPassword1")),
  },
  {
    exact: "true",
    path: "/auth/reset-password-2",
    element: lazy(() => import("./views/auth/reset-password/ResetPassword2")),
  },
  {
    exact: "true",
    path: "/auth/change-password",
    element: lazy(() => import("./views/auth/ChangePassword")),
  },
  {
    exact: "true",
    path: "/auth/create-password",
    element: lazy(() => import("./views/auth/CreatePassword")),
  },
  {
    exact: "true",
    path: "/auth/profile-settings",
    element: lazy(() => import("./views/auth/ProfileSettings")),
  },
  {
    exact: "true",
    path: "/auth/tabs-auth",
    element: lazy(() => import("./views/auth/TabsAuth")),
  },
  {
    exact: "true",
    path: "/products/catalogo/:tienda",
    element: lazy(() => import("./views/products/catalogo")),
    roles: [
      "superUser",
      "Administrador",
      "Gerente",
      "Supervisor",
      "Vendedor",
      "Cajero",
    ],
    module: "Inventario",
  },
  {
    exact: "true",
    guard: StartInfoGuard,
    path: "/auth/start-info",
    element: lazy(() => import("./views/auth/startInfo")),
  },
  {
    path: "*",
    layout: AdminLayout,
    guard: AuthGuard,
    routes: [
      {
        exact: "true",
        guard: RolesGuard,
        path: "/",
        element: lazy(() => import("./views/dashboard/home")),
        roles: [
          "superUser",
          "Administrador",
          "Gerente",
          "Supervisor",
          "Vendedor",
          "Cajero",
          "Mensajero",
        ],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/app/dashboard/analytics",
        element: lazy(() => import("./views/dashboard/DashAnalytics")),
        roles: ["superUser"], //
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/dashboard/analitica",
        element: lazy(() => import("./views/dashboard/analitica")),
        roles: ["superUser"],
      },
      //Modulo facturacion//
      {
        exact: "true",
        guard: RolesGuard,
        path: "/ventas/pos",
        title: "Punto de Venta",
        element: lazy(() => import("./views/ventas/pos")),
        roles: [
          "superUser",
          "Administrador",
          "Gerente",
          "Supervisor",
          "Vendedor",
          "Cajero",
        ],
        module: "Facturación",
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/ventas/checkout",
        title: "Punto de Venta",
        element: lazy(() => import("./views/ventas/Checkout")),
        roles: [
          "superUser",
          "Administrador",
          "Gerente",
          "Supervisor",
          "Vendedor",
          "Cajero",
        ],
        module: "Facturación",
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/ventas/checkout/:id_orden",
        title: "Punto de Venta",
        element: lazy(() => import("./views/ventas/Checkout")),
        roles: [
          "superUser",
          "Administrador",
          "Gerente",
          "Supervisor",
          "Vendedor",
          "Cajero",
        ],
        module: "Facturación",
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/ventas/pos/:id_orden",
        title: "Punto de Venta",
        element: lazy(() => import("./views/ventas/pos")),
        roles: [
          "superUser",
          "Administrador",
          "Gerente",
          "Supervisor",
          "Vendedor",
          "Cajero",
        ],
        module: "Facturación",
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/ventas/cuentas-x-cobrar",
        title: "Cuentas por Cobrar",
        element: lazy(() => import("./views/ventas/cuentasXcobrar")),
        roles: [
          "superUser",
          "Administrador",
          "Gerente",
          "Supervisor",
          "Vendedor",
          "Cajero",
        ],
        module: "Facturación",
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/ventas/caja",
        title: "Control de Cajas",
        element: lazy(() => import("./views/ventas/controlCajas/caja")),
        roles: [
          "superUser",
          "Administrador",
          "Gerente",
          "Supervisor",
          "Vendedor",
          "Cajero",
        ],
        module: "Facturación",
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/ventas/caja/:idCaja",
        title: "Control de Cajas",
        element: lazy(() => import("./views/ventas/controlCajas/caja")),
        roles: [
          "superUser",
          "Administrador",
          "Gerente",
          "Supervisor",
          "Vendedor",
          "Cajero",
        ],
        module: "Facturación",
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/ventas/resumen-pagos",
        title: "Resumen de Pagos",
        element: lazy(() => import("./views/ventas/controlCajas/resumenPagos")),
        roles: [
          "superUser",
          "Administrador",
          "Gerente",
          "Supervisor",
          "Vendedor",
          "Cajero",
        ],
        module: "Facturación",
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/ventas/historial-cajas",
        title: "Historial de Cajas",
        element: lazy(
          () => import("./views/ventas/controlCajas/historialCajas"),
        ),
        roles: [
          "superUser",
          "Administrador",
          "Gerente",
          "Supervisor",
          "Vendedor",
          "Cajero",
        ],
        module: "Facturación",
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/ventas/recibosManuales",
        element: lazy(
          () => import("./views/ventas/controlCajas/recibosManuales"),
        ),
        roles: [
          "superUser",
          "Administrador",
          "Gerente",
          "Supervisor",
          "Vendedor",
          "Cajero",
        ],
        module: "Facturación",
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/ventas/list",
        title: "Historial de Operaciones",
        element: lazy(() => import("./views/ventas/historialOperaciones")),
        roles: [
          "superUser",
          "Administrador",
          "Gerente",
          "Supervisor",
          "Vendedor",
          "Cajero",
        ],
        module: "Facturación",
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/customers/customers-list",
        title: "Lista de Clientes",
        element: lazy(() => import("./views/persons/customersList")),
        roles: [
          "superUser",
          "Administrador",
          "Gerente",
          "Supervisor",
          "Vendedor",
          "Cajero",
        ],
        module: "Facturación",
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/customer/customers-view/:id_cliente",
        title: "Vista de Cliente",
        element: lazy(() => import("./views/persons/CustomerView")),
        roles: [
          "superUser",
          "Administrador",
          "Gerente",
          "Supervisor",
          "Vendedor",
          "Cajero",
        ],
        module: "Facturación",
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/estadisticas/detalle-ventas",
        title: "Detalle de Ventas",
        element: lazy(() => import("./views/estadisticas/DetalleVentas")),
        roles: ["superUser", "Administrador", "Gerente"],
        module: "Facturación",
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/estadisticas/comision",
        title: "Comisión por Venta",
        element: lazy(() => import("./views/estadisticas/Comision")),
        roles: ["superUser", "Administrador", "Gerente"],
        module: "Facturación",
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/estadisticas/resumen-ventas",
        title: "Resumen de Ventas",
        element: lazy(() => import("./views/estadisticas/ResumenVentas")),
        roles: ["superUser", "Administrador", "Gerente"],
        module: "Facturación",
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/estadisticas/indicadores",
        title: "Indicadores",
        element: lazy(() => import("./views/estadisticas/Indicadores")),
        roles: ["superUser", "Administrador", "Gerente"],
        module: "Facturación",
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/config/facturacion",
        title: "Configuración de Facturación",
        element: lazy(() => import("./views/configuration/InvoicingConfig")),
        roles: ["superUser", "Administrador", "Gerente"],
        module: "Facturación",
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/invoice/invoiceFull/:id_orden",
        element: lazy(() => import("./components/PrintReport/pdf/InvoiceFull")),
        roles: [
          "superUser",
          "Administrador",
          "Gerente",
          "Supervisor",
          "Vendedor",
          "Cajero",
        ],
        module: "Facturación",
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/invoice/invoiceSimple/:id_orden",
        element: lazy(() => import("./components/Invoice/invoiceSimple")),
        roles: [
          "superUser",
          "Administrador",
          "Gerente",
          "Supervisor",
          "Vendedor",
          "Cajero",
        ],
        module: "Facturación",
      },
      //Modulo gastos//
      {
        exact: "true",
        guard: RolesGuard,
        path: "/estadisticas/reporte-gastos",
        title: "Reporte de Gastos",
        element: lazy(() => import("./views/estadisticas/ReporteGastos")),
        roles: ["superUser", "Administrador", "Gerente"],
        module: "Gastos",
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/gastos/historial-gastos",
        title: "Historial de Gastos",
        element: lazy(() => import("./views/gastos/historialGastos")),
        roles: ["superUser", "Administrador", "Gerente"],
        module: "Gastos",
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/gastos/presupuesto",
        title: "Presupuesto",
        element: lazy(() => import("./views/gastos/presupuesto")),
        roles: ["superUser", "Administrador", "Gerente"],
        module: "Gastos",
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/gastos/fijos",
        title: "Gastos Fijos",
        element: lazy(() => import("./views/gastos/fijos")),
        roles: ["superUser", "Administrador", "Gerente"],
        module: "Gastos",
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/gastos/gastos-variados",
        title: "Gastos Variados",
        element: lazy(() => import("./views/gastos/historialGastos")),
        roles: ["superUser", "Administrador", "Gerente"],
        module: "Gastos",
      },

      //Modulo Inventario
      {
        exact: "true",
        guard: RolesGuard,
        path: "/products/Products",
        title: "Inventario",
        element: lazy(() => import("./views/products/Products")),
        roles: [
          "superUser",
          "Administrador",
          "Gerente",
          "Supervisor",
          "Vendedor",
          "Cajero",
        ],
        module: "Inventario",
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/products/Inventario",
        title: "Inventario",
        element: lazy(() => import("./views/products/Inventario")),
        roles: [
          "superUser",
          "Administrador",
          "Gerente",
          "Supervisor",
          "Vendedor",
          "Cajero",
        ],
        module: "Inventario",
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/products/view-product/:id_sucursal/:id_producto",
        title: "Vista de Producto",
        element: lazy(() => import("./views/products/viewProducts")),
        roles: [
          "superUser",
          "Administrador",
          "Gerente",
          "Supervisor",
          "Vendedor",
        ],
        module: "Inventario",
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/products/update-product",
        title: "Actualizar Producto",
        element: lazy(() => import("./views/products/Products")),
        roles: [
          "superUser",
          "Administrador",
          "Gerente",
          "Supervisor",
          "Vendedor",
          "Cajero",
        ],
        module: "Inventario",
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/products/lista-imeis",
        title: "Lista de Imeis",
        element: lazy(() => import("./views/products/listaImeis")),
        roles: [
          "superUser",
          "Administrador",
          "Gerente",
          "Supervisor",
          "Vendedor",
          "Cajero",
        ],
        module: "Inventario",
      },
      //Modulo Compras
      {
        exact: "true",
        guard: RolesGuard,
        path: "/compras/compras-panel",
        title: "Panel de Compra",
        element: lazy(() => import("./views/compras/panel-compra")),
        roles: ["superUser", "Administrador", "Gerente", "Supervisor"],
        module: "Compras",
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/compras/compras-panel/:id_orden_compra",
        title: "Panel de Compra",
        element: lazy(() => import("./views/compras/panel-compra")),
        roles: ["superUser", "Administrador", "Gerente", "Supervisor"],
        module: "Compras",
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/compras/historial-compras",
        title: "Historial de Compras",
        element: lazy(() => import("./views/compras/historial-compras")),
        roles: ["superUser", "Administrador", "Gerente", "Supervisor"],
        module: "Compras",
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/compras/cuentas-x-pagar",
        title: "Cuentas por Pagar",
        element: lazy(() => import("./views/compras/cuentas-x-pagar")),
        roles: ["superUser", "Administrador", "Gerente", "Supervisor"],
        module: "Compras",
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/suppliers/suppliers-list",
        title: "Lista de Proveedores",
        element: lazy(() => import("./views/persons/suppliers-list")),
        roles: [
          "superUser",
          "Administrador",
          "Gerente",
          "Supervisor",
          "Vendedor",
          "Cajero",
        ],
        module: "Compras",
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/suppliers/historial-pagos-compras",
        title: "Historial de pagos",
        element: lazy(() => import("./views/compras/historialPagos")),
        roles: [
          "superUser",
          "Administrador",
          "Gerente",
          "Supervisor",
        ],
        module: "Compras",
      },
      //Predeterminado -- todos las tiendas lo tendran
      {
        exact: "true",
        guard: RolesGuard,
        path: "/config/help-tutorials",
        title: "Ayuda y Tutoriales",
        element: lazy(() => import("./views/configuration/tutorials")),
        roles: [
          "superUser",
          "Administrador",
          "Gerente",
          "Supervisor",
          "Cajero",
        ],
        module: "predeterminado",
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/config/general",
        title: "Configuración General",
        element: lazy(() => import("./views/configuration/generalConfig")),
        roles: ["superUser", "Administrador", "Gerente"],
        module: "predeterminado",
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/users/users-management",
        title: "Gestión de Usuarios",
        element: lazy(() => import("./views/admin/usersConfig")),
        roles: ["superUser", "Administrador", "Gerente"],
        module: "predeterminado",
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/users/historial-access",
        title: "Historial de Accesos",
        element: lazy(() => import("./views/admin/historial-accesos")),
        roles: ["superUser", "Administrador", "Gerente"],
        module: "predeterminado",
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/admin-panel/solicitudes",
        title: "Panel de Solicitudes",
        element: lazy(() => import("./views/admin/solicitudes")),
        roles: ["superUser", "Administrador", "Gerente"],
        module: "predeterminado",
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/config/profile-view",
        title: "Vista de Perfil",
        element: lazy(() => import("./views/configuration/ProfileView")),
        roles: [
          "superUser",
          "Administrador",
          "Gerente",
          "Supervisor",
          "Vendedor",
          "Cajero",
        ],
        module: "predeterminado",
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/config/printer",
        title: "Configuración de Impresora",
        element: lazy(() => import("./views/configuration/printerConfig")),
        roles: [
          "superUser",
          "Administrador",
          "Gerente",
          "Supervisor",
          "Cajero",
        ],
        module: "predeterminado",
      },
      //Modulo empleados
      {
        exact: "true",
        guard: RolesGuard,
        path: "/employees-panel",
        title: "Panel de Empleados",
        element: lazy(() => import("./views/employees/employeesPanel")),
        roles: ["superUser", "Administrador", "Gerente"],
        module: "empleados",
      },
      //Modulo para administracion sistema mediante tigery soft
      {
        exact: "true",
        guard: TigerySoftGuard,
        path: "/suscripciones/gestion-servicios",
        title: "Gestión de Servicios",
        element: lazy(() => import("./views/suscripciones/gestionServicios")),
        roles: ["superUser", "Administrador", "Gerente"],
      },
      {
        exact: "true",
        guard: TigerySoftGuard,
        path: "/suscripciones/gestion-suscripciones",
        title: "Gestión de Suscripciones",
        element: lazy(
          () => import("./views/suscripciones/gestionSuscripciones"),
        ),
        roles: ["superUser", "Administrador", "Gerente"],
      },
      {
        exact: "true",
        guard: TigerySoftGuard,
        path: "/suscripciones/planes",
        title: "Planes",
        element: lazy(() => import("./views/suscripciones/planes")),
        roles: ["superUser", "Administrador", "Gerente"],
      },
      //De aqui arriba el kyanUp
      {
        exact: "true",
        guard: RolesGuard,
        path: "/app/dashboard/sales",
        title: "Dashboard de Ventas",
        element: lazy(() => import("./views/dashboard/DashSales")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/app/widgets/widget-statistic",
        title: "Widget de Estadísticas",
        element: lazy(() => import("./views/widgets/WidgetStatistic")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/app/widgets/widget-data",
        element: lazy(() => import("./views/widgets/WidgetData")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/app/widgets/widget-chart",
        element: lazy(() => import("./views/widgets/WidgetChart")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/layout/vertical/static",
        element: lazy(() => import("./views/page-layouts/vertical/Static")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/layout/vertical/fixed",
        element: lazy(() => import("./views/page-layouts/vertical/Fixed")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        path: "/layout/vertical/nav-fixed",
        element: lazy(() => import("./views/page-layouts/vertical/NavFixed")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/layout/vertical/collapse-menu",
        element: lazy(
          () => import("./views/page-layouts/vertical/CollapseMenu"),
        ),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/layout/vertical/v-rtl",
        element: lazy(() => import("./views/page-layouts/vertical/RTLLayout")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/layout/horizontal",
        element: lazy(() => import("./views/page-layouts/horizontal/Static")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/layout/horizontal-v2",
        element: lazy(
          () => import("./views/page-layouts/horizontal/Static-v2"),
        ),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/layout/horizontal-rtl",
        element: lazy(
          () => import("./views/page-layouts/horizontal/RTLLayout"),
        ),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/layout/box",
        element: lazy(() => import("./views/page-layouts/extra-layouts/Box")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/layout/light",
        element: lazy(() => import("./views/page-layouts/extra-layouts/Light")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/layout/dark",
        element: lazy(() => import("./views/page-layouts/extra-layouts/Dark")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/users/user-profile",
        element: lazy(() => import("./views/users/UserProfile")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/users/user-cards",
        element: lazy(() => import("./views/users/UserCard")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/users/user-list",
        element: lazy(() => import("./views/users/UserList")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/basic/alert",
        element: lazy(() => import("./views/ui-elements/basic/BasicAlert")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/basic/button",
        element: lazy(() => import("./views/ui-elements/basic/BasicButton")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/basic/badges",
        element: lazy(() => import("./views/ui-elements/basic/BasicBadges")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/basic/breadcrumb-pagination",
        element: lazy(
          () => import("./views/ui-elements/basic/BasicBreadcrumbPagination"),
        ),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/basic/cards",
        element: lazy(() => import("./views/ui-elements/basic/BasicCards")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/basic/collapse",
        element: lazy(() => import("./views/ui-elements/basic/BasicCollapse")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/basic/carousel",
        element: lazy(() => import("./views/ui-elements/basic/BasicCarousels")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/basic/grid-system",
        element: lazy(
          () => import("./views/ui-elements/basic/BasicGridSystem"),
        ),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/basic/progress",
        element: lazy(() => import("./views/ui-elements/basic/BasicProgress")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/basic/modal",
        element: lazy(() => import("./views/ui-elements/basic/BasicModals")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/basic/spinner",
        element: lazy(() => import("./views/ui-elements/basic/BasicSpinner")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/basic/tabs-pills",
        element: lazy(() => import("./views/ui-elements/basic/BasicTabsPills")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/basic/typography",
        element: lazy(
          () => import("./views/ui-elements/basic/BasicTypography"),
        ),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/basic/tooltip-popovers",
        element: lazy(
          () => import("./views/ui-elements/basic/BasicTooltipsPopovers"),
        ),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/basic/other",
        element: lazy(() => import("./views/ui-elements/basic/BasicOther")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/hospital/hosp-dashboard",
        element: lazy(() => import("./views/panels/hospital/Dashboard")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/hospital/hosp-department",
        element: lazy(() => import("./views/panels/hospital/Department")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/hospital/hosp-doctor",
        element: lazy(() => import("./views/panels/hospital/Doctor")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/hospital/hosp-patient",
        element: lazy(() => import("./views/panels/hospital/Patient")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/hospital/hosp-nurse",
        element: lazy(() => import("./views/panels/hospital/Nurse")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/hospital/hosp-pharmacist",
        element: lazy(() => import("./views/panels/hospital/Pharmacist")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/hospital/hosp-laboratory",
        element: lazy(() => import("./views/panels/hospital/Laboratory")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/helpdesk/hd-dashboard",
        element: lazy(() => import("./views/panels/helpdesk/Dashboard")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/helpdesk/hd-ticket-create",
        element: lazy(() => import("./views/panels/helpdesk/CreateTicket")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/helpdesk/hd-ticket-list",
        element: lazy(() => import("./views/panels/helpdesk/TicketList")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/helpdesk/hd-ticket-details",
        element: lazy(() => import("./views/panels/helpdesk/TicketDetails")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/helpdesk/hd-customer-list",
        element: lazy(() => import("./views/panels/helpdesk/Customer")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/project-crm/pc-dashboard",
        element: lazy(() => import("./views/panels/project-crm/Dashboard")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/project-crm/pc-customers",
        element: lazy(() => import("./views/panels/project-crm/Customers")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/project-crm/pc-project",
        element: lazy(() => import("./views/panels/project-crm/Project")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/project-crm/pc-task",
        element: lazy(() => import("./views/panels/project-crm/Task")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/membership/mshp-dashboard",
        element: lazy(() => import("./views/panels/membership/Dashboard")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/membership/mshp-email",
        element: lazy(() => import("./views/panels/membership/Email")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/membership/mshp-country",
        element: lazy(() => import("./views/panels/membership/Country")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/membership/mshp-coupons",
        element: lazy(() => import("./views/panels/membership/Coupons")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/membership/mshp-newsletter",
        element: lazy(() => import("./views/panels/membership/Newsletter")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/membership/mshp-user",
        element: lazy(() => import("./views/panels/membership/User")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/membership/mshp-membership",
        element: lazy(() => import("./views/panels/membership/Membership")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/school/sch-dashboard",
        element: lazy(() => import("./views/panels/school/Dashboard")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/school/sch-student",
        element: lazy(() => import("./views/panels/school/Student")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/school/sch-parents",
        element: lazy(() => import("./views/panels/school/Parents")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/school/sch-teacher",
        element: lazy(() => import("./views/panels/school/Teacher")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/sis/sis-dashboard",
        element: lazy(() => import("./views/panels/sis/Dashboard")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/sis/sis-leave",
        element: lazy(() => import("./views/panels/sis/Leave")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/sis/sis-evaluation",
        element: lazy(() => import("./views/panels/sis/Evaluation")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/sis/sis-event",
        element: lazy(() => import("./views/panels/sis/Event")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/sis/sis-circular",
        element: lazy(() => import("./views/panels/sis/Circular")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/sis/sis-course",
        element: lazy(() => import("./views/panels/sis/Course")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/crypto/cp-dashboard",
        element: lazy(() => import("./views/panels/crypto/Dashboard")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/crypto/cp-exchange",
        element: lazy(() => import("./views/panels/crypto/Exchange")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/crypto/cp-wallet",
        element: lazy(() => import("./views/panels/crypto/Wallet")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/crypto/cp-transactions",
        element: lazy(() => import("./views/panels/crypto/Transaction")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/crypto/cp-history",
        element: lazy(() => import("./views/panels/crypto/History")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/crypto/cp-trading",
        element: lazy(() => import("./views/panels/crypto/Trading")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/crypto/cp-coin",
        element: lazy(() => import("./views/panels/crypto/InitialCoin")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/crypto/cp-ico",
        element: lazy(() => import("./views/panels/crypto/IcoListing")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/ecommerce/ecomm-product-list",
        element: lazy(() => import("./views/panels/ecommerce/Product")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/ecommerce/ecomm-product-details",
        element: lazy(() => import("./views/panels/ecommerce/ProductDetails")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/ecommerce/ecomm-order",
        element: lazy(() => import("./views/panels/ecommerce/Order")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/ecommerce/ecomm-checkout",
        element: lazy(() => import("./views/panels/ecommerce/Checkout")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/ecommerce/ecomm-cart",
        element: lazy(() => import("./views/panels/ecommerce/Cart")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/ecommerce/ecomm-customer",
        element: lazy(() => import("./views/panels/ecommerce/Customers")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/ecommerce/ecomm-seller",
        element: lazy(() => import("./views/panels/ecommerce/Sellers")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/productos/lista-productos",
        element: lazy(() => import("./views/products/viewProducts")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/advance/sweet-alert",
        element: lazy(() => import("./views/ui-elements/advance/AdvanceAlert")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/advance/datepicker",
        element: lazy(
          () => import("./views/ui-elements/advance/AdvanceDatepicker"),
        ),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/advance/task-board",
        element: lazy(
          () => import("./views/ui-elements/advance/AdvanceTaskBoard"),
        ),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/advance/light-box",
        element: lazy(
          () => import("./views/ui-elements/advance/AdvanceLightBox"),
        ),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/advance/adv-modal",
        element: lazy(() => import("./views/ui-elements/advance/AdvanceModal")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/advance/notification",
        element: lazy(
          () => import("./views/ui-elements/advance/AdvanceNotification"),
        ),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/advance/nestable",
        element: lazy(
          () => import("./views/ui-elements/advance/AdvanceNestable"),
        ),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/advance/p-notify",
        element: lazy(
          () => import("./views/ui-elements/advance/AdvancePNotify"),
        ),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/advance/rating",
        element: lazy(
          () => import("./views/ui-elements/advance/AdvanceRating"),
        ),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/advance/range-slider",
        element: lazy(
          () => import("./views/ui-elements/advance/AdvanceRangeSlider"),
        ),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/advance/slider",
        element: lazy(
          () => import("./views/ui-elements/advance/AdvanceSlider"),
        ),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/advance/syntax-highlighter",
        element: lazy(
          () => import("./views/ui-elements/advance/AdvanceSyntaxHighlighter"),
        ),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/advance/tour",
        element: lazy(() => import("./views/ui-elements/advance/AdvanceTour")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/advance/tree-view",
        element: lazy(() => import("./views/ui-elements/advance/AdvanceTree")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/forms/form-basic",
        element: lazy(() => import("./views/forms/FormsElements")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/forms/form-advance",
        element: lazy(() => import("./views/forms/FormsAdvance")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/forms/form-validation",
        element: lazy(() => import("./views/forms/FormsValidation")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/forms/form-masking",
        element: lazy(() => import("./views/forms/FormsMasking")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/forms/form-wizard",
        element: lazy(() => import("./views/forms/FormsWizard")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/forms/form-picker",
        element: lazy(() => import("./views/forms/FormsPicker")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/forms/form-select",
        element: lazy(() => import("./views/forms/FormsSelect")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/tables/bootstrap",
        element: lazy(() => import("./views/tables/BootstrapTable")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/table/datatable/tbl-basic",
        element: lazy(() => import("./views/tables/react-table/Basic")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/table/datatable/tbl-footer",
        element: lazy(() => import("./views/tables/react-table/Footers")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/table/datatable/tbl-sorting",
        element: lazy(() => import("./views/tables/react-table/Sorting")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/table/datatable/tbl-filtering",
        element: lazy(() => import("./views/tables/react-table/Filtering")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/table/datatable/tbl-grouping",
        element: lazy(() => import("./views/tables/react-table/Grouping")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/table/datatable/tbl-pagination",
        element: lazy(() => import("./views/tables/react-table/Pagination")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/charts/apex-chart",
        element: lazy(() => import("./views/charts/apex-chart")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/charts/chart-js",
        element: lazy(() => import("./views/charts/chart-js")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/charts/highchart",
        element: lazy(() => import("./views/charts/highchart")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/charts/rechart",
        element: lazy(() => import("./views/charts/re-chart")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/maps/google-map",
        element: lazy(() => import("./views/maps/GoogleMaps")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/maps/vector-map",
        element: lazy(() => import("./views/maps/VectorMaps")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/todo/todo-basic",
        element: lazy(() => import("./views/applications/to-do/ToDo")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/task/task-list",
        element: lazy(() => import("./views/applications/task/TaskList")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/task/task-board",
        element: lazy(() => import("./views/applications/task/TaskBoard")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/task/task-detail",
        element: lazy(() => import("./views/applications/task/TaskDetails")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/gallery/gallery-grid",
        element: lazy(() => import("./views/applications/gallery/GalleryGrid")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/gallery/photo-grid",
        element: lazy(() => import("./views/applications/gallery/PhotoGrid")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/gallery/gallery-masonry",
        element: lazy(
          () => import("./views/applications/gallery/MasonryGallery"),
        ),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/editor/ck-editor/ck-classic",
        element: lazy(
          () => import("./views/extensions/editors/ck-editor/EditorCkClassic"),
        ),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/editor/ck-editor/ck-balloon",
        element: lazy(
          () => import("./views/extensions/editors/ck-editor/EditorCkBalloon"),
        ),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/editor/ck-editor/ck-inline",
        element: lazy(
          () => import("./views/extensions/editors/ck-editor/EditorCkInline"),
        ),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/editor/ck-editor/ck-document",
        element: lazy(
          () => import("./views/extensions/editors/ck-editor/EditorCkDocument"),
        ),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/editor/rich-editor",
        element: lazy(() => import("./views/extensions/editors/EditorRichNib")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/editor/jodit-wysiwyg",
        element: lazy(
          () => import("./views/extensions/editors/EditorJoditWYSIWYG"),
        ),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/image-cropper",
        element: lazy(() => import("./views/extensions/ImageCropper")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/file-upload",
        element: lazy(() => import("./views/extensions/FileUpload")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/full-calendar",
        element: lazy(() => import("./views/extensions/FullEventCalendar")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/invoice/invoice-basic",
        element: lazy(() => import("./views/extensions/invoice/InvoiceBasic")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/invoice/invoice-summary",
        element: lazy(
          () => import("./views/extensions/invoice/InvoiceSummary"),
        ),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/invoice/invoice-list",
        element: lazy(() => import("./views/extensions/invoice/InvoiceList")),
        roles: ["superUser"],
      },
      {
        exact: "true",
        guard: RolesGuard,
        path: "/sample-page",
        element: lazy(() => import("./views/extra/SamplePage")),
        roles: ["superUser"],
      },
      {
        path: "*",
        exact: "true",
        guard: RolesGuard,
        element: () => <Navigate to={BASE_URL} />,
      },
    ],
  },
];

export default renderRoutes;
